import { BACKEND_URL } from '../../config/apiBase';
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Copy, ExternalLink, CheckCircle, Clock, FileText,
  DollarSign, User, Phone, Mail, Truck, RefreshCw, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const API = BACKEND_URL;

const DEAL_STATUSES = [
  "deal_created", "loi_generated", "loi_sent", "loi_viewed",
  "activation_pending", "activated", "conditional_approval_issued",
  "documents_pending", "final_review", "approved_for_funding", "funded"
];

const STATUS_LABELS = {
  deal_created: "Deal Created", loi_generated: "LOI Generated", loi_sent: "LOI Sent",
  loi_viewed: "LOI Viewed", activation_pending: "Activation Pending", activated: "Activated",
  conditional_approval_issued: "Approval Issued", documents_pending: "Documents Pending",
  final_review: "Final Review", approved_for_funding: "Approved", funded: "Funded"
};

const DealDetail = () => {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchDeal = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/deals/detail/${dealId}`, { headers });
      setDeal(res.data);
    } catch (err) {
      toast.error('Deal not found');
      navigate('/deals');
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => { fetchDeal(); }, [fetchDeal]);

  const updateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      await axios.put(`${API}/deals/update/${dealId}`, { status: newStatus }, { headers });
      toast.success(`Status updated to ${STATUS_LABELS[newStatus]}`);
      fetchDeal();
    } catch (err) {
      toast.error('Error updating status');
    } finally {
      setUpdating(false);
    }
  };

  const toggleDoc = async (docType, completed) => {
    try {
      await axios.post(`${API}/deals/update-document/${dealId}?doc_type=${docType}&completed=${completed}`, {}, { headers });
      toast.success('Document updated');
      fetchDeal();
    } catch (err) {
      toast.error('Error updating document');
    }
  };

  const handleGenerateLOI = async () => {
    try {
      await axios.post(`${API}/deals/generate-loi/${dealId}`, {}, { headers });
      toast.success('LOI Generated');
      fetchDeal();
    } catch (err) { toast.error('Error'); }
  };

  const handleSendLOI = async () => {
    try {
      const res = await axios.post(`${API}/deals/send-loi/${dealId}`, {}, { headers });
      const url = `${window.location.origin}/activate/${res.data.activation_token}`;
      await navigator.clipboard.writeText(url);
      toast.success('LOI sent! Link copied.');
      fetchDeal();
    } catch (err) { toast.error('Error'); }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!deal) return null;

  const currentIdx = DEAL_STATUSES.indexOf(deal.status);
  const nextStatus = currentIdx < DEAL_STATUSES.length - 1 ? DEAL_STATUSES[currentIdx + 1] : null;
  const docs = deal.documents || {};

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="deal-detail">
      {/* Back */}
      <Button variant="ghost" onClick={() => navigate('/deals')} className="gap-2">
        <ArrowLeft className="w-4 h-4" /> Back to Pipeline
      </Button>

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            {deal.customer?.first_name} {deal.customer?.last_name}
            <Badge className="bg-red-600 text-white">{STATUS_LABELS[deal.status]}</Badge>
          </h1>
          <p className="text-muted-foreground">{deal.customer?.company_name}</p>
        </div>
        <div className="flex gap-2">
          {deal.status === 'deal_created' && (
            <Button onClick={handleGenerateLOI} className="bg-red-600 hover:bg-red-700" data-testid="gen-loi-btn">Generate LOI</Button>
          )}
          {deal.status === 'loi_generated' && (
            <Button onClick={handleSendLOI} className="bg-red-600 hover:bg-red-700" data-testid="send-loi-btn">Send LOI</Button>
          )}
          {['loi_sent', 'loi_viewed', 'activation_pending'].includes(deal.status) && (
            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/activate/${deal.activation_token}`); toast.success('Copied!'); }}>
              <Copy className="w-4 h-4 mr-2" /> Activation Link
            </Button>
          )}
          {nextStatus && (
            <Button variant="outline" onClick={() => updateStatus(nextStatus)} disabled={updating} data-testid="advance-status-btn">
              {updating ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
              Advance to {STATUS_LABELS[nextStatus]}
            </Button>
          )}
        </div>
      </div>

      {/* Status Pipeline */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-1 overflow-x-auto pb-1">
            {DEAL_STATUSES.map((s, i) => (
              <div key={s} className="flex items-center gap-1">
                <div className={cn(
                  "w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0",
                  i < currentIdx ? "bg-emerald-500 text-white" :
                  i === currentIdx ? "bg-red-500 text-white ring-2 ring-red-400" :
                  "bg-muted text-muted-foreground"
                )}>
                  {i < currentIdx ? <CheckCircle className="w-3 h-3" /> : <span className="text-[10px]">{i + 1}</span>}
                </div>
                {i < DEAL_STATUSES.length - 1 && <div className={cn("h-px w-4", i < currentIdx ? "bg-emerald-500" : "bg-muted")} />}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Customer Info */}
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><User className="w-4 h-4" /> Customer</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p><span className="text-muted-foreground">Name:</span> {deal.customer?.first_name} {deal.customer?.last_name}</p>
            <p className="flex items-center gap-2"><Mail className="w-3 h-3 text-muted-foreground" />{deal.customer?.email}</p>
            <p className="flex items-center gap-2"><Phone className="w-3 h-3 text-muted-foreground" />{deal.customer?.phone || 'N/A'}</p>
            <p><span className="text-muted-foreground">Company:</span> {deal.customer?.company_name || 'N/A'}</p>
          </CardContent>
        </Card>

        {/* Equipment */}
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Truck className="w-4 h-4" /> Equipment</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p className="font-medium">{deal.equipment?.description}</p>
            <p><span className="text-muted-foreground">Cost:</span> <span className="font-semibold">${(deal.equipment?.cost || 0).toLocaleString()}</span></p>
            {deal.equipment?.year && <p><span className="text-muted-foreground">Year:</span> {deal.equipment.year}</p>}
            {deal.equipment?.vin && <p><span className="text-muted-foreground">VIN:</span> {deal.equipment.vin}</p>}
          </CardContent>
        </Card>

        {/* Terms */}
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><DollarSign className="w-4 h-4" /> Terms</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p><span className="text-muted-foreground">Down Payment:</span> {deal.terms?.down_payment_pct}% (${(deal.terms?.down_payment_amount || 0).toLocaleString()})</p>
            <p><span className="text-muted-foreground">Origination Fee:</span> ${(deal.terms?.origination_fee || 0).toLocaleString()}</p>
            <p><span className="text-muted-foreground">Term:</span> {deal.terms?.term_months} months</p>
            {deal.terms?.rate && <p><span className="text-muted-foreground">Rate:</span> {deal.terms.rate}%</p>}
            {deal.terms?.monthly_payment && <p><span className="text-muted-foreground">Monthly:</span> ${deal.terms.monthly_payment.toLocaleString()}</p>}
            <p><span className="text-muted-foreground">Program:</span> {deal.terms?.program_type}</p>
          </CardContent>
        </Card>

        {/* Documents Checklist */}
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4" /> Documents</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {[
              { key: 'bill_of_sale', label: 'Bill of Sale' },
              { key: 'insurance', label: 'Insurance' },
              { key: 'title', label: 'Title' },
              { key: 'signed_documents', label: 'Signed Documents' },
            ].map(({ key, label }) => {
              const completed = docs[key] !== null && docs[key] !== undefined;
              return (
                <div key={key} className="flex items-center justify-between p-2 rounded-lg bg-muted/50" data-testid={`doc-${key}`}>
                  <div className="flex items-center gap-2">
                    {completed ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <Clock className="w-4 h-4 text-muted-foreground" />}
                    <span className={cn("text-sm", completed && "line-through text-muted-foreground")}>{label}</span>
                  </div>
                  <Button size="sm" variant={completed ? "ghost" : "outline"} onClick={() => toggleDoc(key, !completed)}>
                    {completed ? 'Undo' : 'Mark Received'}
                  </Button>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Activity Log */}
      <Card>
        <CardHeader><CardTitle className="text-base">Activity Log</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {(deal.activities || []).map((act, i) => (
              <div key={i} className="flex items-start gap-3 text-sm py-2 border-b border-muted last:border-0">
                <Clock className="w-3 h-3 mt-1 text-muted-foreground flex-shrink-0" />
                <div>
                  <span className="font-medium">{act.action?.replace(/_/g, ' ')}</span>
                  {act.details && <span className="text-muted-foreground ml-2">- {act.details}</span>}
                  <p className="text-xs text-muted-foreground">{new Date(act.timestamp).toLocaleString()}</p>
                </div>
              </div>
            ))}
            {(!deal.activities || deal.activities.length === 0) && (
              <p className="text-sm text-muted-foreground">No activity yet</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DealDetail;
